import Flex from "components/grid/Flex";
import { useTranslation } from "react-i18next";
import HistoryContainerAndTitle from "./HistoryContainerAndTitle";
import { isAnyPresent, makeHistory } from "../../HistoryUtils";
import Date from "./Date";
import { Typography } from "@material-ui/core";
import { getContentFullArray } from "./SimpleArrayOfValues";
import Label from "./Label";
import Value from "./Value";
import _ from "lodash";
import YesNoOrChildren from "./YesNoOrChildren";

const toObject = (array) => array.reduce((acc, cur) => ({ ...acc, [cur.question]: cur.answer }), {});

const CancerHistory = ({ anamnesisHistory }) => {
    const { t } = useTranslation();
    const history = makeHistory(anamnesisHistory);
    const cancers = getContentFullArray([
        ...history.getLatestSubDataRows("activeCancerData"),
        ...history.getLatestSubDataRows("pastCancerData"),
    ]).map(toObject);
    const familyCancers = getContentFullArray(history.getLatestSubDataRows("familyCancerData")).map(toObject);
    const isVisible = isAnyPresent(anamnesisHistory, [
        "activeCancer",
        "pastCancer",
        "familyCancer",
        "activeCancerData",
        "pastCancerData",
        "familyCancerData",
        "nightSweats",
        "weightLoss",
        "fever",
    ]);
    return (
        <HistoryContainerAndTitle title={t(`anamnesis.cancer.title`)} isVisible={isVisible}>
            <Flex item container grow={0} column>
                <YesNoOrChildren
                    {...{
                        label: t(`global.cancer`),
                        switchValue: history.getLatestValueOf("activeCancer") || history.getLatestValueOf("pastCancer"),
                        arrayValue: [
                            ...history.getLatestSubDataRows("activeCancerData"),
                            ...history.getLatestSubDataRows("pastCancerData"),
                        ],
                    }}
                >
                    {cancers?.map((cancer, index) => (
                        <Flex item container grow={0} key={cancer.value + index} style={{ marginBottom: 10 }}>
                            <Flex item grow={0} style={{ minWidth: 150 }}>
                                <Typography style={{ fontWeight: "bold" }}>{`Z.n. ${t(`global.cancer`)}`}</Typography>
                            </Flex>
                            <Flex item container column>
                                <Flex item container grow={0}>
                                    {cancer.date && (
                                        <Date
                                            date={cancer.date}
                                            style={{ alignItems: "none", marginRight: "10px", minWidth: 150 }}
                                        />
                                    )}
                                    <Typography style={{ fontWeight: "bold" }}>{cancer.value}</Typography>
                                </Flex>
                                {(cancer.radiationDose ||
                                    cancer.radiationSessionsInTotal ||
                                    cancer.radiationSessionsPerWeek ||
                                    cancer.radiotherapyDate) && (
                                    <Flex item container grow={0} gap={10} alignItems={"center"}>
                                        <Typography style={{ minWidth: 150 }}>
                                            {t("anamnesis.cancer.radiation")}:
                                        </Typography>
                                        <Date date={cancer.radiotherapyDate} />
                                        {cancer.radiotherapyToDate && (
                                            <>
                                                - <Date date={cancer.radiotherapyToDate} />
                                            </>
                                        )}
                                        {cancer.radiationDose && (
                                            <>
                                                <Label>{t("anamnesis.cancer.dose")}</Label>
                                                <Value>{cancer.radiationDose}</Value>
                                            </>
                                        )}

                                        {cancer.radiationSessionsInTotal && (
                                            <>
                                                <Label>{t("anamnesis.cancer.all-sessions")}</Label>
                                                <Value>{cancer.radiationSessionsInTotal}</Value>
                                            </>
                                        )}
                                        {cancer.radiationSessionsPerWeek && (
                                            <>
                                                <Label>{t("anamnesis.cancer.weekly-sessions")}</Label>
                                                <Value>{cancer.radiationSessionsPerWeek}</Value>
                                            </>
                                        )}
                                    </Flex>
                                )}
                                {(cancer.chemotherapyCycle ||
                                    cancer.chemoSubstance ||
                                    cancer.chemotherapySessionsInTotal ||
                                    cancer.chemotherapyFromDate) && (
                                    <Flex item container grow={0} gap={10} alignItems={"center"}>
                                        <Typography style={{ minWidth: 150 }}>Chemotherapy:</Typography>
                                        <Date date={cancer.chemotherapyFromDate} />
                                        {cancer.chemotherapyToDate && (
                                            <>
                                                - <Date date={cancer.chemotherapyToDate} />
                                            </>
                                        )}
                                        {cancer.chemoSubstance && (
                                            <>
                                                <Label>{t("anamnesis.cancer.substance")}</Label>
                                                <Value>{cancer.chemoSubstance}</Value>
                                            </>
                                        )}
                                        {cancer.chemotherapySessionsInTotal && (
                                            <>
                                                <Label>{t("anamnesis.cancer.all-sessions")}</Label>
                                                <Value>{cancer.chemotherapySessionsInTotal}</Value>
                                            </>
                                        )}
                                        {cancer.chemotherapyCycle && (
                                            <>
                                                <Label>{t("anamnesis.cancer.cycle")}</Label>
                                                <Value>{cancer.chemotherapyCycle}</Value>
                                            </>
                                        )}
                                    </Flex>
                                )}
                                {cancer.surgeryDate && (
                                    <Flex item container grow={0} gap={10} alignItems={"center"}>
                                        <Typography style={{ minWidth: 150 }}>
                                            {t("anamnesis.cancer.surgery")}:
                                        </Typography>
                                        <Date date={cancer.surgeryDate} />
                                    </Flex>
                                )}
                            </Flex>
                        </Flex>
                    ))}
                </YesNoOrChildren>

                {["nightSweats", "weightLoss", "fever"].some((value) => history.getLatestValueOf(value)) && (
                    <Flex item container grow={0} gap={10} alignItems={"center"}>
                        <Typography
                            style={{
                                marginLeft: 75,
                                fontWeight: "bold",
                            }}
                        >
                            {t("anamnesis.cancer.b-symptoms")}
                        </Typography>
                        {["nightSweats", "weightLoss", "fever"].map((value) =>
                            history.getLatestValueOf(value) ? (
                                <Typography>{" " + t(`anamnesis.cancer.${value + "Button"}`)}</Typography>
                            ) : (
                                <></>
                            ),
                        )}
                    </Flex>
                )}

                <YesNoOrChildren
                    {...{
                        label: t(`anamnesis.cancer.familyCancer`),
                        switchValue: history.getLatestValueOf("familyCancer"),
                        arrayValue: history.getLatestSubDataRows("familyCancerData"),
                    }}
                >
                    <Flex item container grow={0}>
                        {!_.isEmpty(familyCancers) && (
                            <Typography style={{ fontWeight: "bold", minWidth: 150 }}>
                                {t("anamnesis.cancer.familyCancer")}
                            </Typography>
                        )}
                        <Flex item container grow={0} column>
                            {familyCancers?.map((cancer, index) => (
                                <Flex item container grow={0} gap={10} alignItems={"center"} key={cancer.value + index}>
                                    <Typography>{t(`anamnesis.cancer.familyCancerData.${cancer.who}`)}</Typography>
                                    <Typography>{cancer.kind}</Typography>
                                    <Date date={cancer.date} />
                                </Flex>
                            ))}
                        </Flex>
                    </Flex>
                </YesNoOrChildren>
            </Flex>
        </HistoryContainerAndTitle>
    );
};

export default CancerHistory;
